import { TEnrollmentStatus } from '@api/io/session.enrollment.io';
import { TSelfenrollmentStatus } from '@api/io/selfreg.enrollment.io';
import { useMemo } from 'react';
import { FeedbackUIAction, publish, TCoreEvents } from '@fe-core-events';
import { useIntl } from 'react-intl';
import { useConfigContext } from '@fe-core';

type CompoundEnrollmentStatus = TEnrollmentStatus | TSelfenrollmentStatus;

export const useEnrollmentFeedback = ({
  sessionId,
  enrollmentStatus,
  overrideStartCourseLink,
}: {
  sessionId: number | string;
  enrollmentStatus?: CompoundEnrollmentStatus | null;
  overrideStartCourseLink?: string;
}) => {
  const intl = useIntl();
  const { config } = useConfigContext();

  const eventMap: Record<
    CompoundEnrollmentStatus,
    {
      eventType: 'warning' | 'info' | 'success';
      eventAction: FeedbackUIAction;
      title: string;
      description: string;
    }
  > = useMemo(() => {
    const closeAction = {
      event: '',
      type: 'close' as const,
      text: {
        title: intl.formatMessage({ id: 'learner.common.close' }),
      },
    };
    const startCourseAction = {
      event:
        overrideStartCourseLink ||
        `${config.baseUrls.aleweb}session/${sessionId}`,
      type: 'link' as const,
      text: {
        title: intl.formatMessage({ id: 'catalog.course.action.start' }),
      },
    };

    return {
      member: {
        eventType: 'success',
        title: 'catalog.course.enroll.confirmation.member.title',
        description: 'catalog.course.enroll.confirmation.member.description',
        eventAction: startCourseAction,
      },
      requested: {
        eventType: 'info',
        title: 'catalog.course.enroll.confirmation.requested.title',
        description: 'catalog.course.enroll.confirmation.requested.description',
        eventAction: closeAction,
      },
      waitlistmember: {
        eventType: 'info',
        title: 'catalog.course.waitlist.confirmation.title',
        description: 'catalog.course.waitlist.confirmation.description',
        eventAction: closeAction,
      },
      alreadymember: {
        eventType: 'info',
        title: 'catalog.course.enroll.confirmation.alreadymember.title',
        description:
          'catalog.course.enroll.confirmation.alreadymember.description',
        eventAction: closeAction,
      },
      overlimit: {
        eventType: 'info',
        title: 'catalog.course.enroll.confirmation.overlimit.title',
        description: 'catalog.course.enroll.confirmation.overlimit.description',
        eventAction: closeAction,
      },
      unknown: {
        eventType: 'warning',
        title: 'catalog.course.enroll.confirmation.unknown.title',
        description: 'catalog.course.enroll.confirmation.unknown.description',
        eventAction: closeAction,
      },
    };
  }, [config, intl, overrideStartCourseLink, sessionId]);

  if (!enrollmentStatus) {
    return;
  }

  const eventDetails = eventMap[enrollmentStatus];
  const eventName = `feedback.ui.${eventDetails.eventType}.base` as TCoreEvents;
  publish({
    eventName,
    data: {
      consumer: 'fe-learner-catalog',
      event: eventName,
      type: 'modal',
      status: eventDetails.eventType,
      config: {
        title: intl.formatMessage({ id: eventDetails.title }),
        description: intl.formatMessage({ id: eventDetails.description }),
      },
      action: eventDetails.eventAction,
    },
  });
};
